import React, { useMemo } from 'react';
import { PALLET_WIDTH, PALLET_LENGTH } from '../../utils/palletCalculation';

export const Pallet2DVisualizer = ({ arrangement, transparentBackground = false }) => {
    // Calculate scale to fit the container
    const padding = 60; // Reduced padding to maximize view
    const bottomLabelSpace = 200; // Increased space for text below the pallet (was 100)
    const viewBoxWidth = PALLET_WIDTH + padding * 2;
    // Increase height to accommodate bottom labels
    const viewBoxHeight = PALLET_LENGTH + padding + bottomLabelSpace;

    const boxRects = useMemo(() => {
        if (!arrangement) return [];
        const rects = [];

        // Case 1: Pre-calculated layout (e.g. from mixed orientation optimization)
        if (arrangement.layerLayout && arrangement.layerLayout.length > 0) {
            return arrangement.layerLayout.map((box, index) => ({
                x: box.x,
                y: box.y,
                width: box.width,
                length: box.length,
                id: index
            }));
        }

        // Case 2: Standard grid layout
        // We can derive the grid from boxesInWidth and boxesInLength
        // Note: The arrangement object has boxWidth and boxLength which might be swapped relative to the input dimensions
        // depending on the arrangement type.
        const cols = arrangement.boxesInWidth;
        const rows = arrangement.boxesInLength;
        const bWidth = arrangement.boxWidth;
        const bLength = arrangement.boxLength;

        for (let i = 0; i < cols; i++) {
            for (let j = 0; j < rows; j++) {
                rects.push({
                    x: i * bWidth,
                    y: j * bLength,
                    width: bWidth,
                    length: bLength,
                    id: `${i}-${j}`
                });
            }
        }

        return rects;
    }, [arrangement]);

    if (!arrangement) {
        return (
            <div className="h-full w-full flex items-center justify-center text-stone-500 bg-stone-900 rounded-lg border border-stone-700">
                No arrangement selected
            </div>
        );
    }

    return (
        <div
            className={`h-full w-full relative rounded-lg border border-stone-700 overflow-hidden ${transparentBackground ? '' : 'bg-stone-950'}`}
            style={{ width: '100%', height: '100%', position: 'relative' }}
        >
            <div
                className="absolute inset-0 flex flex-col"
                style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column' }}
            >
                {!transparentBackground && (
                    <div className="p-2 bg-stone-900 border-b border-stone-800 flex justify-between items-center shrink-0">
                        <span className="text-xs font-bold text-stone-300 uppercase">2D Visualisierung - {arrangement.name}</span>
                    </div>
                )}
                <div
                    className={`flex-1 p-4 flex items-center justify-center overflow-hidden ${transparentBackground ? '' : ''}`}
                    style={{ flex: 1, padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                >
                    <svg
                        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
                        className="max-w-full max-h-full drop-shadow-2xl"
                        style={{ maxHeight: '100%', maxWidth: '100%' }}
                    >
                        {/* Background / Pallet Base */}
                        <rect
                            x={padding}
                            y={padding}
                            width={PALLET_WIDTH}
                            height={PALLET_LENGTH}
                            fill={transparentBackground ? "none" : "#1a1a1a"}
                            stroke="#333"
                            strokeWidth="2"
                        />

                        {/* Dimensions Text - Top */}
                        <text x={padding + PALLET_WIDTH / 2} y={padding - 15} textAnchor="middle" fill="#666" fontSize="30" fontWeight="bold">
                            {PALLET_WIDTH}mm
                        </text>

                        {/* Dimensions Text - Left */}
                        <text
                            x={padding - 15}
                            y={padding + PALLET_LENGTH / 2}
                            textAnchor="middle"
                            fill="#666"
                            fontSize="30"
                            fontWeight="bold"
                            transform={`rotate(-90, ${padding - 15}, ${padding + PALLET_LENGTH / 2})`}
                        >
                            {PALLET_LENGTH}mm
                        </text>

                        {/* Boxes Group - Translated by padding */}
                        <g transform={`translate(${padding}, ${padding})`}>
                            {boxRects.map((box) => (
                                <rect
                                    key={box.id}
                                    x={box.x}
                                    y={box.y}
                                    width={box.width}
                                    height={box.length}
                                    fill="rgba(30, 58, 138, 0.2)" // Blue-900 with 20% opacity
                                    stroke="#1e3a8a" // Blue-900
                                    strokeWidth="2"
                                />
                            ))}
                        </g>

                        {/* Footer Info inside SVG - Positioned relative to pallet bottom */}
                        <text x={viewBoxWidth / 2} y={padding + PALLET_LENGTH + 35} textAnchor="middle" fill="#444" fontSize="30">
                            Europalette (800mm x 1200mm)
                        </text>
                        <text x={viewBoxWidth / 2} y={padding + PALLET_LENGTH + 75} textAnchor="middle" fill="#444" fontSize="30">
                            Boxen pro Layer: {arrangement.boxesPerLayer} ({arrangement.boxesInWidth} x {arrangement.boxesInLength})
                        </text>
                    </svg>
                </div>
            </div>
        </div>
    );
};
